import { BigNumber } from "bignumber.js";

import type { SemanticExpr, SympyResult } from "./types";

type NumberNode = Extract<SemanticExpr, { type: "number" }>;
type OperatorNode = Extract<SemanticExpr, { type: "operator" }>;
type FunctionNode = Extract<SemanticExpr, { type: "function" }>;

function num(value: string, kind: "integer" | "float"): NumberNode {
  return { type: "number", kind, value: value };
}

function op(name: string, args: (SemanticExpr | SemanticExpr[])[]): OperatorNode {
  return { type: "operator", name, args } as OperatorNode;
}

/**
 * Normalization strength.
 *
 * `weak` applies only rewrites that DO NOT change the visible/rendered representation (plus a few
 * interchangeable alternative forms like `exp(x)` ↔ `e^x`).
 *
 * `strong` = `weak` PLUS the presentation-changing rewrites SymPy's raw output needs to become a
 * natural rendered form.
 */
type NormalizeMode = "weak" | "strong";

/**
 * Wrap in `Neg`. In `strong` mode, collapse double negation and drop the sign on zero — both change
 * the visible form (`-(-1)` → `1`), so they are withheld in `weak` mode.
 */
function makeNeg(e: SemanticExpr, mode: NormalizeMode): SemanticExpr {
  if (mode === "strong") {
    if (e.type === "operator" && e.name === "Neg") return e.args[0];
    if (e.type === "number" && BigNumber(e.value).isEqualTo(0)) return num("0", "integer");
  }
  return op("Neg", [e]);
}

/** Split a normalized expression into a sign flag and its non-negated core. */
function stripSign(e: SemanticExpr): { neg: boolean; inner: SemanticExpr } {
  if (e.type === "operator" && e.name === "Neg") return { neg: true, inner: e.args[0] };
  return { neg: false, inner: e };
}

/** Strip a single leading `-` from a numeric value string ("-5" → "5", "5" → "5"). */
function absValue(value: string): string {
  return BigNumber(value).abs().toString();
}

function isNegative(value: string): boolean {
  return BigNumber(value).isNegative();
}

function normalizeNumber(node: NumberNode, mode: NormalizeMode): SemanticExpr {
  const n = BigNumber(node.value);

  const abs = num(n.abs().toString(), node.kind);
  return n.isNegative() ? makeNeg(abs, mode) : abs;
}

/** `rational{p,q}` → natural `Frac(p, q)` WITHOUT reducing; a leading sign is lifted into `Neg`. */
function normalizeRational(p: string, q: string, mode: NormalizeMode): SemanticExpr {
  const neg = isNegative(p) !== isNegative(q);
  const frac = op("Frac", [num(absValue(p), "integer"), num(absValue(q), "integer")]);
  return neg ? makeNeg(frac, mode) : frac;
}

function isOne(e: SemanticExpr): boolean {
  return e.type === "number" && e.kind === "integer" && BigNumber(e.value).isEqualTo(1);
}

/**
 * Normalize `Pow(base, exp)` toward natural form, inspecting the RAW exponent for the negative /
 * unit-fraction shapes SymPy uses (mirrors `parsePower`). The negative-exponent → reciprocal and
 * unit-fraction → root rewrites change the rendering, so they are `strong`-only; `weak` keeps `Pow`.
 */
function normalizePow(
  baseRaw: SemanticExpr,
  expRaw: SemanticExpr,
  mode: NormalizeMode,
): SemanticExpr {
  const base = normalizeSemanticMode(baseRaw, mode);

  // Unit powers keep their signed integer exponent (m/s² = Pow(second, -2)) so parseSympyResult can
  // combine them into a composite unit — do NOT rewrite to Frac/Root. Collapse only the exp-1 case.
  if (base.type === "unit") {
    if (isOne(expRaw)) {
      return base;
    }
    return op("Pow", [
      base,
      expRaw.type === "number" ? expRaw : normalizeSemanticMode(expRaw, mode),
    ]);
  }

  if (mode === "strong") {
    // Negative integer exponent → reciprocal: Pow(b, -n) → Frac(1, Pow(b, n)).
    if (expRaw.type === "number" && expRaw.kind === "integer" && isNegative(expRaw.value)) {
      return op("Frac", [
        num("1", "integer"),
        normalizePow(baseRaw, num(absValue(expRaw.value), "integer"), mode),
      ]);
    }
    // Negative rational exponent → reciprocal.
    if (expRaw.type === "rational" && isNegative(expRaw.p)) {
      return op("Frac", [
        num("1", "integer"),
        normalizePow(baseRaw, { type: "rational", p: absValue(expRaw.p), q: expRaw.q }, mode),
      ]);
    }
    // Explicitly-negated exponent (input `Neg(...)`) → reciprocal.
    if (expRaw.type === "operator" && expRaw.name === "Neg") {
      return op("Frac", [num("1", "integer"), normalizePow(baseRaw, expRaw.args[0], mode)]);
    }
    // Unit-fraction exponent → root: Pow(b, 1/q) → Root(b, q).
    if (expRaw.type === "rational" && expRaw.p === "1") {
      return op("Root", [base, num(expRaw.q, "integer")]);
    }
    // Exponent of 1 → the base itself.
    if (isOne(expRaw)) {
      return base;
    }
  }
  return op("Pow", [base, normalizeSemanticMode(expRaw, mode)]);
}

/** A `unit` leaf or `Pow(unit, n)` — the factors parseSympyResult combines into a composite unit. */
function isUnitFactor(e: SemanticExpr): boolean {
  if (e.type === "unit") return true;
  return e.type === "operator" && e.name === "Pow" && e.args[0]?.type === "unit";
}

function mulOf(factors: SemanticExpr[]): SemanticExpr {
  if (factors.length === 0) return num("1", "integer");
  if (factors.length === 1) return factors[0];
  return op("Mul", factors);
}

/**
 * Normalize a `Mul`, lifting the aggregate sign into a single `Neg` and dropping the multiplicative
 * identity (both `weak`). In `strong` mode it also collects reciprocal factors (`Frac(1, d)`, i.e.
 * SymPy's `Pow(d, -1)`) into one denominator so `Mul(a, Pow(b, -1))` becomes the natural `Frac(a, b)`
 * (mirrors `parseMultiplication`) — a rendering change, so `weak` keeps the flat product.
 */
function normalizeMul(rawArgs: SemanticExpr[], mode: NormalizeMode): SemanticExpr {
  let neg = false;
  const inners: SemanticExpr[] = [];
  for (const raw of rawArgs) {
    const { neg: n, inner } = stripSign(normalizeSemanticMode(raw, mode));
    if (n) neg = !neg;
    if (isOne(inner)) continue; // drop the multiplicative identity
    inners.push(inner);
  }

  let result: SemanticExpr;
  if (mode === "weak" || inners.some(isUnitFactor)) {
    // Weak: keep the product flat (no reciprocal folding — that changes rendering).
    // Strong unit-bearing product: keep it flat (including Pow(unit, -n)) so parseSympyResult
    // combines the units into a composite unit (e.g. m/s) rather than a plain fraction.
    result = mulOf(inners);
  } else {
    // Collect reciprocal factors (Frac(1, d), i.e. SymPy's Pow(d, -1)) into one denominator.
    const numerators: SemanticExpr[] = [];
    const denominators: SemanticExpr[] = [];
    for (const inner of inners) {
      if (inner.type === "operator" && inner.name === "Frac" && isOne(inner.args[0])) {
        denominators.push(inner.args[1]);
      } else {
        numerators.push(inner);
      }
    }
    result =
      denominators.length > 0
        ? op("Frac", [mulOf(numerators), mulOf(denominators)])
        : mulOf(numerators);
  }
  return neg ? makeNeg(result, mode) : result;
}

function normalizeOperator(node: OperatorNode, mode: NormalizeMode): SemanticExpr {
  switch (node.name) {
    case "Neg":
      return makeNeg(normalizeSemanticMode(node.args[0], mode), mode);

    case "Mul":
      return normalizeMul(node.args, mode);

    case "Frac":
      // a/b keeps its natural shape (no reduction); only lift an aggregate sign.
      return canonicalizeFrac(node.args[0], node.args[1], mode);

    case "Pow":
      return node.args.length === 2
        ? normalizePow(node.args[0], node.args[1], mode)
        : passthrough(node, mode);

    case "Root":
      // A bare √ carries no index argument; defaulting it to 2 does not change the rendering, so it
      // applies in both modes.
      return op("Root", [
        normalizeSemanticMode(node.args[0], mode),
        node.args[1] === undefined
          ? num("2", "integer")
          : normalizeSemanticMode(node.args[1], mode),
      ]);

    case "Exp1":
      // Euler's number stays an atom (renders as `e`); `exp(x)` folds into `Pow(Exp1, x)` — see
      // normalizeFunction. This keeps the display-friendly `e` / `e^x` forms.
      return op("Exp1", []);

    default:
      return passthrough(node, mode);
  }
}

/** Frac(a, b) natural form: normalize both sides, lift an aggregate sign, no reduction. */
function canonicalizeFrac(
  aRaw: SemanticExpr,
  bRaw: SemanticExpr,
  mode: NormalizeMode,
): SemanticExpr {
  const a = stripSign(normalizeSemanticMode(aRaw, mode));
  const b = stripSign(normalizeSemanticMode(bRaw, mode));
  const frac = op("Frac", [a.inner, b.inner]);
  return a.neg !== b.neg ? makeNeg(frac, mode) : frac;
}

function passthrough(node: OperatorNode, mode: NormalizeMode): SemanticExpr {
  return op(
    node.name,
    node.args.map((arg) => {
      return Array.isArray(arg)
        ? arg.map((a) => normalizeSemanticMode(a, mode))
        : normalizeSemanticMode(arg, mode);
    }),
  );
}

function normalizeFunction(node: FunctionNode, mode: NormalizeMode): SemanticExpr {
  const { name } = node;

  // exp(x) ↔ e^x: fold into the display-friendly Pow(Exp1, x) so it matches input `e^x`. Although
  // this changes the rendering, `exp(x)` and `e^x` are treated as interchangeable alternative forms,
  // so the fold applies in both modes (weak included).
  if (name === "sp.exp" && node.args.length === 1) {
    return normalizePow(op("Exp1", []), node.args[0] as SemanticExpr, mode);
  }
  return {
    type: "function",
    name,
    args: node.args.map((arg) => {
      return Array.isArray(arg)
        ? arg.map((a) => normalizeSemanticMode(a, mode))
        : normalizeSemanticMode(arg, mode);
    }),
  } as FunctionNode;
}

/** Normalize a single SemanticExpr at the given strength. */
function normalizeSemanticMode(expr: SemanticExpr, mode: NormalizeMode): SemanticExpr {
  switch (expr.type) {
    case "number":
      return normalizeNumber(expr, mode);
    case "rational":
      return normalizeRational(expr.p, expr.q, mode);
    case "symbol":
    case "imaginary_unit":
    case "infinity":
    case "unit":
      return expr;
    case "plus_minus":
      return {
        type: "plus_minus",
        real: normalizeSemanticMode(expr.real, mode),
        imag: normalizeSemanticMode(expr.imag, mode),
      };
    case "matrix":
      return { ...expr, data: expr.data.map((d) => normalizeSemanticMode(d, mode)) };
    case "function":
      return normalizeFunction(expr, mode);
    case "operator":
      return normalizeOperator(expr, mode);
    default:
      throw new Error(`Unexpected SemanticExpr type: ${JSON.stringify(expr)}`);
  }
}

/** Strong normalization: canonical natural form for SymPy OUTPUT (see {@link NormalizeMode}).
 *
 * @example
 * Mul(-1, x) → Neg(x)
 * Pow(x, -n) → Frac(1, Pow(x, n))
 * */
export function normalizeSemantic(expr: SemanticExpr): SemanticExpr {
  return normalizeSemanticMode(expr, "strong");
}

/** Weak normalization: representation-preserving canonicalization for INPUT (see {@link NormalizeMode}).
 *
 * @example
 * Mul(Neg(2), x) → Neg(Mul(2, x))   // sign relocation, same rendering
 * Pow(x, Neg(2)) → Pow(x, Neg(2))   // NOT rewritten to Frac (that changes rendering)
 * */
export function weakNormalizeSemantic(expr: SemanticExpr): SemanticExpr {
  return normalizeSemanticMode(expr, "weak");
}

/**
 * Canonical comparison key for an ALREADY-normalized tree. Commutative operands (`Add`, `Mul`) are
 * sorted here so that reordering does not affect equality — the sort exists only in this string, it
 * never touches a returned tree.
 */
function comparisonKey(e: SemanticExpr): string {
  switch (e.type) {
    case "symbol":
      return `sym:${e.name}`;
    case "number":
      return `num:${e.kind}:${e.value}`;
    case "rational":
      return `rat:${e.p}/${e.q}`;
    case "imaginary_unit":
      return "i";
    case "infinity":
      return `inf:${e.kind}`;
    case "unit":
      return `unit:${e.name}`;
    case "plus_minus":
      return `pm(${comparisonKey(e.real)},${comparisonKey(e.imag)})`;
    case "matrix":
      return `mat:${e.rows}x${e.cols}[${e.data.map(comparisonKey).join(",")}]`;
    case "function":
      return `fn:${e.name}(${e.args
        .map((arg) => {
          return Array.isArray(arg) ? arg.map(comparisonKey).join(",") : comparisonKey(arg);
        })
        .join(",")})`;
    case "operator": {
      const keys = e.args.map((arg) => {
        return Array.isArray(arg) ? arg.map(comparisonKey).join(",") : comparisonKey(arg);
      });
      if (e.name === "Add" || e.name === "Mul") keys.sort();
      return `${e.name}(${keys.join(",")})`;
    }
  }
}

/** Normalize a `SympyResult` (single expr or list, e.g. multiple `solve` solutions). */
export function normalizeResult<T extends SympyResult>(result: T): T {
  return Array.isArray(result)
    ? (result.map(normalizeResult) as T)
    : (normalizeSemantic(result) as T);
}

/**
 * Structural equivalence of two semantic trees, tolerant of commutative reordering only.
 *
 * Note that this is not semantical equivalence in the mathematical sense
 * (e.g. `Add(a, b) === Add(b, a)`, but: `Mul(a, Frac(b, c)) !== Frac(Mul(a, b), c)`)
 */
export function semanticEquals(a: SympyResult, b: SympyResult): boolean {
  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray || bIsArray) {
    if (!aIsArray || !bIsArray || a.length !== b.length) return false;
    return a.every((item, i) => semanticEquals(item, b[i]));
  }
  return comparisonKey(a) === comparisonKey(b);
}
